import { defineStore, acceptHMRUpdate } from 'pinia';
import { DialogLevel } from '~/types/ui';
declare global {
  interface window {
    onScroll: () => void;
  }
}

export const useDialogStore = defineStore('DialogStore', () => {
  if (!useAppRunningStore().isRunning) return;
  const isOpened = ref<boolean>(false);

  const view = shallowRef<Component>();
  const props = ref<any>();
  const viewport = ref<DOMRect>(null);
  const queue: Ref<DialogProp[]> = ref([]);

  // locked level is set to system during app setup
  const lockedLevel = ref<
    | DialogLevel.System
    | DialogLevel.Settings
    | DialogLevel.Info
    | DialogLevel.Reward
    | DialogLevel.All
  >(DialogLevel.All);

  const lockLevel = (
    level:
      | DialogLevel.System
      | DialogLevel.Settings
      | DialogLevel.Info
      | DialogLevel.Reward
      | DialogLevel.All
  ) => {
    lockedLevel.value = level;
  };

  const _enqueue = (dialogAttr: DialogProp) => {
    queue.value.push({
      dialog: markRaw(dialogAttr.dialog),
      data: dialogAttr.data,
    });
  };

  const unlockedDialogsCount = computed(() => {
    return queue.value.filter((item) => {
      return item.data.level <= lockedLevel.value;
    }).length;
  });

  const lockOnSystemLevel = () => {
    lockLevel(DialogLevel.System);
  };

  const lockOnSettingsLevel = () => {
    lockLevel(DialogLevel.Settings);
  };

  const lockInfoLevel = () => {
    lockLevel(DialogLevel.Info);
  };

  const unlock = () => {
    lockLevel(DialogLevel.All);
  };

  const open = (dialog: Component, data: any) => {
    if (data.level == null) {
      data.level = DialogLevel.Info;
    }
    _enqueue({ dialog, data });
    if (data.level > lockedLevel.value) return;

    if (isOpened.value && currentDialogLevel.value >= data.level) {
      close();
    }
  };

  const currentDialogLevel = computed(() => {
    if (props.value) return props.value.level;
    return DialogLevel.All;
  });

  const pickDialog = (): DialogProp => {
    if (unlockedDialogsCount.value === 0) return;
    // pick dialog with unlocked level with and with the lowest level.
    const list = queue.value.map((item, index) => {
      return { level: item.data.level, index };
    });
    const filtered = list.filter((item) => item.level <= lockedLevel.value);
    const maxLevel = filtered.sort((a, b) => a.level - b.level);
    const indexDialog = maxLevel[0].index;
    const dialogProps = queue.value.splice(indexDialog, 1)[0];
    isOpened.value = true;
    view.value = dialogProps.dialog;
    props.value = dialogProps.data;
  };

  const close = () => {
    view.value = null;
    props.value = null;
  };

  const closeComplete = () => {
    isOpened.value = false;
  };

  const setViewport = (rect) => {
    viewport.value = rect;
  };

  const canPickDialog = computed(() => {
    return unlockedDialogsCount.value > 0 && !isOpened.value;
  });

  watch(
    () => canPickDialog.value,
    (bool) => {
      if (bool) {
        pickDialog();
      }
    }
  );

  return {
    isOpened,
    open,
    unlockedDialogsCount,
    lockedLevel,
    currentDialogLevel,
    queue,
    close,
    view,
    props,
    setViewport,
    viewport,
    closeComplete,
    lockOnSystemLevel,
    lockOnSettingsLevel,
    lockInfoLevel,
    unlock,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDialogStore, import.meta.hot));
}
